/**
 * See my Price block — interactive behavior.
 *
 * State machine driven by the `data-state` attribute on the block root.
 * All rate calculation delegates to GhpRateService; this file only knows
 * about DOM wiring, the AGIA membership REST call, and sessionStorage.
 *
 * Boot: imported + instantiated per .ghp-smp root from main.js.
 */

import { Modal } from "bootstrap";
import GhpRateService from "../services/ghp-rate-service.js";
import { GHP_SESSION_KEY } from "../ghp/constants.js";

const MEMBERSHIP_CHECK_URL = "/wp-json/ghp/v1/check-membership";
const ENROLL_FAMILY_PATH = "ghp/v1/enroll-family";
const MEMBERSHIP_PATTERN = /^\d{10}$/;
// Minimum time (ms) the loading state stays visible before transitioning to
// success or error. Gives the user a consistent sense of progress and avoids
// flashing the success panel when the pipeline resolves near-instantly.
const MIN_LOADING_MS = 3300;

class SeeMyPriceBlock {
  constructor(root) {
    this.root = root;
    this.productId = Number(root.dataset.productId) || 0;
    this.rateService = new GhpRateService(this.productId || null);
    this.nextUrl = root.dataset.nextUrl || "#";

    this.form = root.querySelector(".ghp-smp__form");
    this.submitBtn = root.querySelector(".ghp-smp__submit");
    this.membershipInput = root.querySelector('input[name="membership"]');
    this.ageInput = root.querySelector('input[name="age"]');
    this.stateSelect = root.querySelector('select[name="state"]');
    this.amountEl = root.querySelector(".ghp-smp__amount");
    this.successPanel = root.querySelector(".ghp-smp__success");
    this.successHeading = root.querySelector(".ghp-smp__success-heading");
    this.loadingTexts = root.querySelectorAll(".ghp-smp__loading-text");

    // Error UI lives in a Bootstrap modal rendered by ModalManager at the
    // page footer. The block tells us which modal id is its companion via
    // the data-error-modal attribute (per-block id, see see-my-price.php).
    const errorModalId = root.dataset.errorModal || "";
    this.errorModalEl = errorModalId ? document.getElementById(errorModalId) : null;
    this.errorModal = this.errorModalEl ? Modal.getOrCreateInstance(this.errorModalEl) : null;

    // Per-submission cache of validation state so the CTA handler doesn't
    // recompute the rate or re-call AGIA.
    this.lastResult = null;

    // Minimum-loading-duration bookkeeping.
    this.loadingStartedAt = null;
    this.pendingTransition = null;

    this.bind();
    this._prefillFromUrl();
  }

  _prefillFromUrl() {
    const raw = new URLSearchParams(window.location.search).get("ean") ?? "";
    const digits = raw.replace(/\D/g, "");
    if (MEMBERSHIP_PATTERN.test(digits) && this.membershipInput) {
      this.membershipInput.value = digits;
      const url = new URL(window.location.href);
      url.searchParams.delete("ean");
      window.history.replaceState({}, "", url.pathname + (url.search || ""));
    }
  }

  bind() {
    if (this.form) {
      this.form.addEventListener("submit", (e) => this.onSubmit(e));
    }
    if (this.ageInput) {
      this.ageInput.addEventListener("input", () => this.onAgeInput());
    }
    if (this.membershipInput) {
      this.membershipInput.addEventListener("input", () => this.onMembershipInput());
      this.membershipInput.addEventListener("blur", () => this.onMembershipBlur());
    }
    this.root.querySelectorAll(".ghp-smp__back").forEach((btn) => {
      btn.addEventListener("click", () => this.onBack());
    });
    this.root.querySelectorAll(".ghp-smp__cta").forEach((btn) => {
      btn.addEventListener("click", (e) => this.onCta(e));
    });

    // Persistent listeners for the Bootstrap error modal: any dismissal
    // (X, ESC, backdrop click, "Go Back" button) routes through onBack so
    // the form is reset and focus returns to the submit button. Focus
    // moves to the visible error heading on open for screen readers.
    if (this.errorModalEl) {
      this.errorModalEl.addEventListener("shown.bs.modal", () => {
        const heading = this.errorModalEl.querySelector(
          ".ghp-smp__error-msg:not([hidden]) .ghp-smp__error-heading"
        );
        if (heading) {
          heading.setAttribute("tabindex", "-1");
          heading.focus({ preventScroll: false });
        }
      });
      this.errorModalEl.addEventListener("hidden.bs.modal", () => this.onBack());
    }
  }

  onAgeInput() {
    let val = this.ageInput.value;

    // Strip leading zeros ("012" → "12") and enforce 3-char max.
    if (val !== "") {
      const stripped = String(parseInt(val, 10));
      val = Number.isNaN(parseInt(val, 10)) ? val : stripped.slice(0, 3);
      if (val !== this.ageInput.value) {
        this.ageInput.value = val;
      }
    }

    const num = Number(val);
    if (val !== "" && !Number.isNaN(num) && num > 100) {
      this.setFieldError(this.ageInput, "Please enter a valid age.");
    } else {
      this.ageInput.removeAttribute("aria-invalid");
      const errEl = document.getElementById(this.ageInput.id + "-error");
      if (errEl) {
        errEl.hidden = true;
        errEl.textContent = "";
      }
    }
  }

  onMembershipInput() {
    // Strip anything that isn't a digit, enforced on every keystroke.
    const cleaned = this.membershipInput.value.replace(/\D/g, "");
    if (cleaned !== this.membershipInput.value) {
      this.membershipInput.value = cleaned;
    }
    // Clear the error while the user is still typing.
    this.membershipInput.removeAttribute("aria-invalid");
    const errEl = document.getElementById(this.membershipInput.id + "-error");
    if (errEl) {
      errEl.hidden = true;
      errEl.textContent = "";
    }
  }

  onMembershipBlur() {
    const val = this.membershipInput.value;
    if (val !== "" && !MEMBERSHIP_PATTERN.test(val)) {
      this.setFieldError(this.membershipInput, "Membership number must be exactly 10 digits.");
    }
  }

  // ---------------------------------------------------------------------
  // State transitions

  setState(state, errorCode) {
    // Any new state supersedes a scheduled deferred transition.
    if (this.pendingTransition) {
      clearTimeout(this.pendingTransition);
      this.pendingTransition = null;
    }

    // Entering loading: record start time so automatic exits can honor the
    // minimum loading duration below.
    if (state === "loading") {
      this.loadingStartedAt = Date.now();
      this.applyState(state, errorCode);
      return;
    }

    // Defer automatic exits from loading (→ success / error) so the loading
    // state stays visible for at least MIN_LOADING_MS. User-initiated exits
    // (back button → idle) transition immediately.
    const isAutoExit = state === "success" || state === "error";
    if (
      isAutoExit &&
      this.root.dataset.state === "loading" &&
      this.loadingStartedAt !== null
    ) {
      const elapsed = Date.now() - this.loadingStartedAt;
      const remaining = MIN_LOADING_MS - elapsed;
      if (remaining > 0) {
        this.pendingTransition = setTimeout(() => {
          this.pendingTransition = null;
          this.applyState(state, errorCode);
        }, remaining);
        return;
      }
    }

    this.applyState(state, errorCode);
  }

  applyState(state, errorCode) {
    this.root.dataset.state = state;

    if (state === "error") {
      this.showErrorCode(errorCode || null);
      this.openErrorModal();
      // Focus is moved to the visible heading by the shown.bs.modal
      // listener in bind(), so we don't need to focus here.
      return;
    }

    if (state === "success") {
      const target = this.successHeading || this.successPanel;
      if (target) {
        target.setAttribute("tabindex", "-1");
        target.focus({ preventScroll: false });
      }
    }

    if (state === "idle" && this.submitBtn) {
      this.submitBtn.focus({ preventScroll: false });
    }
  }

  showErrorCode(code) {
    if (!this.errorModalEl) {
      return;
    }
    const msgs = this.errorModalEl.querySelectorAll(".ghp-smp__error-msg");
    msgs.forEach((msg) => {
      const isMatch = msg.dataset.code === code;
      msg.toggleAttribute("hidden", !isMatch);
    });

    // Update aria-labelledby / aria-describedby so the modal announces
    // the active variant rather than the first one rendered in the DOM.
    const visible = this.errorModalEl.querySelector(
      ".ghp-smp__error-msg:not([hidden])"
    );
    const heading = visible ? visible.querySelector(".ghp-smp__error-heading") : null;
    const description = visible
      ? visible.querySelector(".ghp-smp__error-description")
      : null;
    if (heading && heading.id) {
      this.errorModalEl.setAttribute("aria-labelledby", heading.id);
    }
    if (description && description.id) {
      this.errorModalEl.setAttribute("aria-describedby", description.id);
    } else {
      this.errorModalEl.removeAttribute("aria-describedby");
    }
  }

  openErrorModal() {
    if (this.errorModal) {
      this.errorModal.show();
    }
  }

  onBack() {
    this.setState("idle");
  }

  clearFieldErrors() {
    [this.membershipInput, this.ageInput, this.stateSelect].forEach((input) => {
      if (!input) {
        return;
      }
      input.removeAttribute("aria-invalid");
      const errEl = document.getElementById(input.id + "-error");
      if (errEl) {
        errEl.hidden = true;
        errEl.textContent = "";
      }
    });
  }

  setFieldError(input, message) {
    if (!input) {
      return;
    }
    input.setAttribute("aria-invalid", "true");
    const errEl = document.getElementById(input.id + "-error");
    if (errEl) {
      errEl.hidden = false;
      errEl.textContent = message;
    }
  }

  validateFields() {
    this.clearFieldErrors();
    let firstInvalid = null;
    const fail = (input, message) => {
      this.setFieldError(input, message);
      if (!firstInvalid) firstInvalid = input;
    };

    const ageRaw = this.ageInput ? this.ageInput.value.trim() : "";
    const ageNum = Number(ageRaw);
    if (ageRaw === "" || Number.isNaN(ageNum) || ageNum < 0 || ageNum > 100) {
      fail(this.ageInput, "Please enter a valid age.");
    }

    const stateVal = this.stateSelect ? this.stateSelect.value : "";
    if (!stateVal) {
      fail(this.stateSelect, "Please choose your state.");
    }

    const memVal = this.membershipInput
      ? this.membershipInput.value.trim()
      : "";
    if (memVal !== "" && !MEMBERSHIP_PATTERN.test(memVal)) {
      fail(this.membershipInput, "Membership number must be exactly 10 digits.");
    }

    if (firstInvalid) {
      firstInvalid.focus({ preventScroll: false });
      return null;
    }

    return {
      age: ageNum,
      state: stateVal.toUpperCase(),
      membership: memVal === "" ? null : memVal,
    };
  }

  onSubmit(event) {
    event.preventDefault();

    if (this.root.dataset.state === "loading") {
      return;
    }
    if (this.productId < 1 || !this.rateService.valid) {
      this.setState("error", "service_unavailable");
      return;
    }

    const inputs = this.validateFields();
    if (!inputs) {
      return;
    }

    this.setLoadingPhase("calculating");
    this.setState("loading");

    // Snapshot inputs so later DOM edits can't bleed in.
    const age = inputs.age;
    const state = inputs.state;
    const membership = inputs.membership;

    // Step A: cheap client-side validation.
    const rateResult = this.rateService.validateAndGetRate(age, state);
    if (rateResult.error) {
      if (
        rateResult.code === "state_not_eligible" ||
        rateResult.code === "age_not_eligible"
      ) {
        this.setState("error", rateResult.code);
      } else {
        this.setState("error", "service_unavailable");
      }
      return;
    }

    // Step B: AGIA membership check (only when a number was provided).
    if (membership === null) {
      this.renderSuccess({
        age: age,
        state: state,
        membership: null,
        membershipStatus: "skipped",
        rate: rateResult.rate,
        ageBand: rateResult.ageBand,
      });
      return;
    }

    fetch(MEMBERSHIP_CHECK_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ membership_number: membership }),
      credentials: "same-origin",
    })
      .then((response) => {
        // Stale guard: user may have clicked "back" mid-flight.
        if (this.root.dataset.state !== "loading") {
          return Promise.reject(new Error("stale"));
        }
        if (!response.ok) {
          this.setState("error", "service_unavailable");
          return Promise.reject(new Error("http_" + response.status));
        }
        return response.json();
      })
      .then((body) => {
        if (this.root.dataset.state !== "loading") {
          return;
        }
        const membershipState = body && body.state;
        if (membershipState === "active") {
          this.renderSuccess({
            age: age,
            state: state,
            membership: membership,
            membershipStatus: "active",
            rate: rateResult.rate,
            ageBand: rateResult.ageBand,
          });
          return;
        }
        if (membershipState === "not_found") {
          if (this.membershipInput) {
            this.membershipInput.setAttribute("aria-invalid", "true");
          }
          this.setState("error", "membership_not_found");
          return;
        }
        if (membershipState === "inactive") {
          if (this.membershipInput) {
            this.membershipInput.setAttribute("aria-invalid", "true");
          }
          this.setState("error", "membership_inactive");
          return;
        }
        this.setState("error", "service_unavailable");
      })
      .catch((err) => {
        if (err && err.message === "stale") {
          return;
        }
        // eslint-disable-next-line no-console
        console.warn("[see-my-price] AGIA membership check failed", err);
        if (this.root.dataset.state !== "loading") {
          return;
        }
        this.setState("error", "service_unavailable");
      });
  }

  renderSuccess(result) {
    this.lastResult = result;
    if (this.amountEl) {
      this.amountEl.textContent = this.formatRate(result.rate);
    }
    this.setState("success");
  }

  formatRate(value) {
    const num = Number(value);
    if (!Number.isFinite(num)) return "0";
    return Number.isInteger(num) ? String(num) : num.toFixed(2);
  }

  onCta(event) {
    const btn = event.currentTarget;
    const intent = btn.dataset.intent === "family" ? "family" : "solo";

    if (!this.lastResult) {
      // Defensive: CTA should only be reachable from success state.
      this.setState("error", "service_unavailable");
      return;
    }

    const payload = {
      productId: this.productId,
      membership: this.lastResult.membership,
      membershipStatus: this.lastResult.membershipStatus,
      age: this.lastResult.age,
      state: this.lastResult.state,
      rate: this.lastResult.rate,
      ageBand: this.lastResult.ageBand,
      intent: intent,
      backUrl: window.location.pathname + window.location.search,
    };

    try {
      sessionStorage.setItem(GHP_SESSION_KEY, JSON.stringify(payload));
    } catch (err) {
      // Quota / private mode — log and continue. The next-step page will
      // re-collect what it needs.
      // eslint-disable-next-line no-console
      console.warn("[see-my-price] sessionStorage write failed", err);
    }

    if (intent === "family") {
      if (!this.nextUrl || this.nextUrl === "#") {
        // eslint-disable-next-line no-console
        console.warn("[see-my-price] next-step URL is not configured; skipping redirect.");
        return;
      }
      window.location.assign(this.nextUrl);
      return;
    }

    // Solo: call enroll-family directly to skip the /ghp-enrollment/ page.
    const api = window.ghpApi || null;
    if (!api || !api.root || !api.nonce) {
      this.setState("error", "enrollment_failed");
      return;
    }

    this.setLoadingPhase("enrolling");
    this.setState("loading");

    const enrollUrl = api.root.replace(/\/$/, "") + "/" + ENROLL_FAMILY_PATH;

    fetch(enrollUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-WP-Nonce": api.nonce,
      },
      body: JSON.stringify({
        productId: this.productId,
        primary: {
          age: this.lastResult.age,
          state: this.lastResult.state,
          membership: this.lastResult.membership,
        },
        family: [],
      }),
      credentials: "same-origin",
    })
      .then((response) => {
        if (this.root.dataset.state !== "loading") {
          return Promise.reject(new Error("stale"));
        }
        if (!response.ok) {
          this.setState("error", "enrollment_failed");
          return Promise.reject(new Error("http_" + response.status));
        }
        return response.json();
      })
      .then((body) => {
        if (this.root.dataset.state !== "loading") {
          return;
        }
        const redirect = body && body.redirect;
        if (!redirect) {
          this.setState("error", "enrollment_failed");
          return;
        }
        const elapsed = this.loadingStartedAt ? Date.now() - this.loadingStartedAt : MIN_LOADING_MS;
        const remaining = Math.max(0, MIN_LOADING_MS - elapsed);
        setTimeout(() => window.location.assign(redirect), remaining);
      })
      .catch((err) => {
        if (err && err.message === "stale") {
          return;
        }
        // eslint-disable-next-line no-console
        console.warn("[see-my-price] enroll-family call failed", err);
        if (this.root.dataset.state !== "loading") {
          return;
        }
        this.setState("error", "enrollment_failed");
      });
  }

  setLoadingPhase(phase) {
    this.loadingTexts.forEach((el) => {
      el.hidden = el.dataset.phase !== phase;
    });
  }
}

export default SeeMyPriceBlock;

// Auto-boot: instantiate one block per .ghp-smp root present on the page.
document.querySelectorAll(".ghp-smp").forEach((root) => new SeeMyPriceBlock(root));
